import { readRefreshedAccessToken } from './readRefreshedAccessToken';
import { getTokens, isUsableRefreshToken, setTokens } from './tokens';

/**
 * Path of the Scorpius token-refresh endpoint, relative to whatever scorpius
 * base URL the transport is configured with. This is the only place it appears.
 */
export const REFRESH_ACCESS_TOKEN_PATH = 'user/api-token-auth/refresh';

/**
 * Issues the refresh POST. Injected rather than chosen here because the callers
 * genuinely cannot share one HTTP client: one goes through the scorpius axios
 * instance, one through RTK Query's `fetchBaseQuery` (which needs the query
 * `api` and `extraOptions` threaded through it), and one through a bare
 * `axios.post`.
 *
 * A transport may either return a failure or throw one. RTK Query returns
 * `{ error }` and never throws; axios throws and never returns an error field.
 * Both are handled, so an axios transport needs no try/catch of its own.
 */
export type RefreshTransport = (
  path: string,
  body: { refresh_token: string },
) => Promise<{ data?: unknown; error?: unknown } | undefined>;

export type RefreshResult =
  /** A token came back and has been persisted. */
  | { status: 'refreshed'; token: string }
  /** Nothing usable to refresh with, so no request was made. */
  | { status: 'no-refresh-token' }
  /** 2xx, but the body carried no usable token. Nothing was persisted. */
  | { status: 'no-token-in-response'; data: unknown }
  /**
   * The request failed. `failure` is whatever the transport surfaced — an RTK
   * Query result object or a thrown axios error — passed through
   * uninterpreted, because deciding what a failure means is the caller's job
   * and each caller already knows its own transport.
   */
  | { status: 'request-failed'; failure: unknown };

/**
 * De-duplicates concurrent refreshes across every caller in the bundle.
 *
 * A single access-token expiry faults every request in flight at that moment,
 * and those requests are spread across the sockets, the RTK Query reauth path
 * and the midas interceptor. Without one shared promise each of them POSTs the
 * same refresh token.
 *
 * Not keyed on anything: the endpoint refreshes the one access token for the
 * one signed-in user, so there is nothing to key on.
 */
let inFlight: Promise<RefreshResult> | null = null;

/** Test seam. Production discards the module on the navigation out. */
export const resetRefreshDedupeForTesting = () => {
  inFlight = null;
};

async function performRefresh(
  refreshToken: string,
  transport: RefreshTransport,
): Promise<RefreshResult> {
  let response;
  try {
    response = await transport(REFRESH_ACCESS_TOKEN_PATH, {
      refresh_token: refreshToken,
    });
  } catch (error) {
    return { status: 'request-failed', failure: error };
  }

  if (response?.error) {
    return { status: 'request-failed', failure: response };
  }

  const token = readRefreshedAccessToken(response?.data);
  if (!token) {
    return { status: 'no-token-in-response', data: response?.data };
  }

  // The endpoint answers with the access token alone and never rotates the
  // refresh token, so the stored one is carried forward. Spreading what is
  // already in storage keeps any other key in the blob rather than replacing
  // the whole thing with these two fields.
  setTokens({ ...(getTokens() || {}), token, refresh_token: refreshToken });

  return { status: 'refreshed', token };
}

/**
 * Refreshes the access token and persists the result: the one place that
 * guards the refresh token, POSTs, validates that a token actually came back,
 * and writes it.
 *
 * Returns a result rather than throwing, so each caller can keep its own
 * failure contract — an outcome value, a dispatched action, or a rejected
 * promise. Knows nothing about Redux; callers dispatch.
 *
 * @param args.refreshToken - Passed in rather than read from storage, because
 *   `refreshUserToken`'s callers supply the value they hold in Redux and that
 *   can differ from what is in storage.
 * @param args.transport - Issues the POST. See {@link RefreshTransport}.
 * @param args.onRequestStart - Called once the refresh token has been accepted
 *   and a refresh is going to be awaited, so a caller can announce that it
 *   started. Fires for callers that join an in-flight refresh too: a caller
 *   that dispatches a start action needs it to pair with the success action it
 *   will get either way.
 */
export async function refreshAccessToken({
  refreshToken,
  transport,
  onRequestStart,
}: {
  refreshToken: unknown;
  transport: RefreshTransport;
  onRequestStart?: () => void;
}): Promise<RefreshResult> {
  // A missing or empty refresh token is the one thing this endpoint answers
  // with a 400 rather than a 401, so asking here saves a round trip whose only
  // purpose is to be told. IRP sessions and legacy `?access_token=` auto-login
  // links store no refresh token, and older writes left the string 'null'.
  if (!isUsableRefreshToken(refreshToken)) {
    return { status: 'no-refresh-token' };
  }

  onRequestStart?.();

  if (!inFlight) {
    inFlight = performRefresh(refreshToken, transport).finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

export default refreshAccessToken;
